import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, AlertTriangle, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import api, { formatError } from "../lib/api";
import { MD_COMPONENTS, REMARK_PLUGINS } from "../lib/markdown";

export const CalendarAiModal = ({ event, open, onClose, locked = false }) => {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState("");
  const [signals, setSignals] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !event || locked) return;
    let active = true;
    setLoading(true);
    setError("");
    setAnalysis("");
    setSignals([]);
    api
      .post("/economic-calendar/ai", {
        title: event.title,
        currency: event.currency,
        date: event.date,
        time: event.time,
        impact: event.impact,
        actual: event.actual,
        forecast: event.forecast,
        previous: event.previous,
      })
      .then((r) => {
        if (!active) return;
        setAnalysis(r.data.analysis || "");
        setSignals(r.data.signals_considered || []);
      })
      .catch((e) => {
        if (active) setError(formatError(e?.response?.data?.detail));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.key, locked]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="calendar-ai-modal"
        className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#0f0f0f] border-white/10 text-zinc-100"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-400">
            <Sparkles size={18} /> News AI
          </DialogTitle>
        </DialogHeader>

        {event && (
          <div className="text-sm text-zinc-400 -mt-1 mb-2">
            <span className="text-zinc-200 font-medium">{event.title}</span> · {event.currency} ·{" "}
            {event.date} {event.time}
          </div>
        )}

        {locked && (
          <div className="flex flex-col items-center text-center gap-3 py-8" data-testid="calendar-ai-locked">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <Lock className="text-emerald-400" size={22} />
            </div>
            <p className="text-sm text-zinc-300"><span className="text-emerald-300 font-medium">News AI</span> è riservata agli abbonati VIP.</p>
            <p className="text-xs text-zinc-500">Analizza dati macro e segnali per prendere decisioni migliori.</p>
            <Link to="/pricing" data-testid="calendar-ai-upgrade" className="mt-1 text-xs font-medium px-4 py-2 rounded-full bg-emerald-500 text-black hover:bg-emerald-400 transition-colors">
              Passa a VIP
            </Link>
          </div>
        )}

        {!locked && loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-zinc-400" data-testid="calendar-ai-loading">
            <Loader2 className="animate-spin text-emerald-400" size={28} />
            <p className="text-sm">Analisi in corso…</p>
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center gap-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-3 text-sm" data-testid="calendar-ai-error">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {!loading && !error && analysis && (
          <div data-testid="calendar-ai-analysis" className="text-sm leading-relaxed">
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MD_COMPONENTS}>
              {analysis}
            </ReactMarkdown>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
